/**
 * The scrubber: Base UI's slider for the drag and the keyboard, with the comment pins and the A-B
 * band laid over it, and a frame preview on hover — decoded in the browser by a second, hidden
 * `<video>` seeking to the hovered time, so no storyboard is generated anywhere. That second
 * element loads nothing until the scrubber is first hovered: most openings never ask for a frame.
 */

import { Slider } from '@base-ui/react/slider';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { formatClock } from '~/entities/exercise-clip';

import { useVideoClock, type VideoClock } from '../lib/useClipPlayback';
import type { LoopRange } from '../model/playback';
import type { PlayerPin } from '../model/playerView';

import styles from './ClipPlayer.module.css';

interface PlayerScrubberProps {
  readonly clock: VideoClock | null;
  readonly duration: number;
  readonly loop: LoopRange;
  readonly pins: readonly PlayerPin[];
  readonly videoUrl: string | null;
  readonly onSeek: (seconds: number) => void;
}

const percent = (seconds: number, duration: number) =>
  duration > 0 ? `${String((Math.min(seconds, duration) / duration) * 100)}%` : '0%';

export function PlayerScrubber({ clock, duration, loop, pins, videoUrl, onSeek }: PlayerScrubberProps) {
  const { t, i18n } = useTranslation();
  const durationMs = Math.max(1, Math.round(duration * 1000));
  const value = useVideoClock(clock, (seconds) => Math.min(Math.round(seconds * 1000), durationMs));
  const preview = useRef<HTMLVideoElement>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [armed, setArmed] = useState(false);
  // The latest hovered time: the preview seeks to it once its current seek lands, and the readout
  // takes it once per frame, however fast the pointer moves.
  const target = useRef(0);
  const sought = useRef(0);
  const frame = useRef(0);

  const seekPreview = (element: HTMLVideoElement) => {
    sought.current = target.current;
    element.currentTime = target.current;
  };

  useEffect(() => {
    const element = preview.current;
    if (element === null) return undefined;
    const onSeeked = () => {
      if (sought.current !== target.current) seekPreview(element);
    };
    element.addEventListener('seeked', onSeeked);
    return () => {
      element.removeEventListener('seeked', onSeeked);
    };
  }, [videoUrl]);

  useEffect(
    () => () => {
      cancelAnimationFrame(frame.current);
    },
    [],
  );

  const hover = (event: PointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (bounds.width <= 0 || duration <= 0) return;
    const fraction = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
    target.current = fraction * duration;
    if (preview.current !== null && !preview.current.seeking) seekPreview(preview.current);
    if (frame.current === 0) {
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        setHovered(target.current);
      });
    }
  };

  return (
    <Slider.Root
      className={styles.scrub}
      largeStep={10_000}
      max={durationMs}
      min={0}
      onValueChange={(value) => {
        onSeek(value / 1000);
      }}
      step={5_000}
      value={value}
    >
      <Slider.Control
        className={styles.scrubControl}
        onPointerEnter={() => {
          setArmed(true);
        }}
        onPointerLeave={() => {
          cancelAnimationFrame(frame.current);
          frame.current = 0;
          setHovered(null);
        }}
        onPointerMove={hover}
      >
        <Slider.Track className={styles.rail}>
          <Slider.Indicator className={styles.fill} />
        </Slider.Track>
        {loop.from === null || loop.to === null ? null : (
          <span
            className={styles.loopBand}
            style={{
              insetInlineStart: percent(loop.from, duration),
              inlineSize: `calc(${percent(loop.to, duration)} - ${percent(loop.from, duration)})`,
            }}
          />
        )}
        {pins.map((pin) => (
          <button
            aria-label={pin.label}
            className={styles.pin}
            key={pin.commentId}
            onClick={() => {
              onSeek(pin.seconds);
            }}
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            style={{ insetInlineStart: percent(pin.seconds, duration) }}
            tabIndex={-1}
            type="button"
          />
        ))}
        <Slider.Thumb
          aria-label={t('web:clipPlayer.controls.scrubber')}
          className={styles.head}
          getAriaValueText={(_, value) =>
            `${formatClock(value / 1000, false, i18n.language)} / ${formatClock(duration, false, i18n.language)}`
          }
        />
        <div
          aria-hidden="true"
          className={`${String(styles.hover)} ${hovered === null ? String(styles.hoverHidden) : ''}`}
          style={{ insetInlineStart: percent(hovered ?? 0, duration) }}
        >
          {videoUrl === null ? null : (
            <video
              className={styles.hoverVideo}
              muted
              playsInline
              preload={armed ? 'auto' : 'none'}
              ref={preview}
              src={videoUrl}
              tabIndex={-1}
            />
          )}
          <div className={styles.hoverTime}>{formatClock(hovered ?? 0, true, i18n.language)}</div>
        </div>
      </Slider.Control>
    </Slider.Root>
  );
}
